import {
  getPublicacionById,
  PublicacionData,
  updatePublicacion,
} from "@/api/PublicacionesService";
import { subirImagenes } from "@/api/UploadService";
import { useUserStore } from "@/store/userStore";
import  { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Alert, AlertDescription } from "../ui/alert";
import { Loader2, Image, ArrowLeft } from "lucide-react";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";

type FormInputs = {
  titulo: string;
  descripcion: string;
};

export default function PublicacionEditar() {
  const { id } = useParams();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormInputs>({
    defaultValues: {
      titulo: "",
      descripcion: "",
    },
  });
  const user = useUserStore((state) => state.user);
  const [uploading, setUploading] = useState(false);
  const [imagenes, setImagenes] = useState<string[]>([]);
  const success = false;
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      getPublicacionById(parseInt(id))
        .then((data) => {
          reset({
            titulo: data.titulo,
            descripcion: data.descripcion,
          });
          setImagenes(data.imagenes.map((img) => img.url_imagen));
        })
        .catch((error) => {
          console.error("Error al cargar la publicación:", error);
          toast.error("Error al cargar la publicación para editar.");
        });
    }
  }, [id, reset]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);

    try {
      const urls = await subirImagenes(Array.from(files));
      setImagenes((prev) => [...prev, ...urls]);
    } catch (err) {
      toast.error("Error al subir las imágenes. Intente nuevamente.");
      console.error("Error al subir imágenes:", err);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index: number) => {
    setImagenes((prev) => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: FormInputs) => {
    try {
      if (!user) throw new Error("Usuario no autenticado");

      const payload: PublicacionData = {
        ...data,
        usuario_id: user.id,
        imagenes: imagenes.map((url_imagen) => ({ url_imagen })),
      };

      if (!id) throw new Error("ID no encontrado");
      await updatePublicacion(parseInt(id), payload);
      toast.success("Publicación editada con éxito");
      navigate(`/publicaciones/${id}`);
    } catch (error) {
      console.error("Error al editar la publicación:", error);
      toast.error("Ocurrió un error al editar la publicación");
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <button
        type="button"
        onClick={() => navigate(id ? `/publicaciones/${id}` : "/publicaciones")}
        className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-sky-900 transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Volver
      </button>

      <div className="space-y-2 mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-sky-900">
          Editar publicación
        </h1>
        <p className="text-gray-600">
          Comparta información relevante con la comunidad
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {success && (
          <Alert>
            <AlertDescription className="text-green-600">
              Publicada con éxito!
            </AlertDescription>
          </Alert>
        )}

        {/* Campo Título */}
        <div className="space-y-2">
          <Label htmlFor="titulo">Título *</Label>
          <Input
            id="titulo"
            {...register("titulo", {
              required: "El título es obligatorio",
              minLength: {
                value: 5,
                message: "El título debe tener al menos 5 caracteres",
              },
            })}
            placeholder="Escriba el título de la publicación"
            className={errors.titulo ? "border-red-500" : ""}
          />
          {errors.titulo && (
            <p className="text-sm text-red-600">{errors.titulo.message}</p>
          )}
        </div>

        {/* Campo Contenido */}
        <div className="space-y-2">
          <Label htmlFor="descripcion">Contenido *</Label>
          <Textarea
            id="descripcion"
            {...register("descripcion", {
              required: "El contenido es obligatorio",
              minLength: {
                value: 20,
                message: "El contenido debe tener al menos 20 caracteres",
              },
            })}
            className={`min-h-[150px] ${
              errors.descripcion ? "border-red-500" : ""
            }`}
            placeholder="Desarrolle el contenido de la publicación..."
          />
          {errors.descripcion && (
            <p className="text-sm text-red-600">{errors.descripcion.message}</p>
          )}
        </div>

        {/* Campo Imágenes */}
        <div className="space-y-2">
          <Label htmlFor="imagenes">Imágenes (opcional)</Label>
          <div className="flex items-center gap-4">
            <Label
              htmlFor="imagenes"
              className="flex items-center gap-2 px-4 py-2 border rounded-md cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <Image className="w-5 h-5 text-gray-500" />
              <span>{uploading ? "Subiendo..." : "Seleccionar imágenes"}</span>
            </Label>
            <Input
              id="imagenes"
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              disabled={uploading}
              className="hidden"
            />
            {uploading && <Loader2 className="animate-spin text-gray-500" />}
          </div>
          <p className="text-sm text-gray-500">Máx. 10 imágenes (JPEG, PNG)</p>

          {imagenes.length > 0 && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {imagenes.map((imgUrl, idx) => (
                <div key={idx} className="relative group">
                  <img
                    src={imgUrl}
                    alt={`Imagen ${idx + 1}`}
                    className="w-full h-32 object-cover rounded-lg border"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(idx)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Botón de envío */}
        <div className="pt-4">
          <Button
            type="submit"
            disabled={isSubmitting || uploading}
            className="w-full sm:w-auto"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Publicando...
              </>
            ) : (
              "Editar publicación"
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
